import { writePaymentReturnWorkspaceId } from '@/utils/paymentReturnWorkspace';
import { isPluginProduct } from '@common/api/productContext';

export const PAYMENT_RETURN_ROUTE = '/payment/return';
export const PAYMENT_RETURN_PUBLIC_PATH = '/admin/payment/return';
export const STRIPE_CHECKOUT_SESSION_ID_TEMPLATE = '{CHECKOUT_SESSION_ID}';
export const ONBOARDING_PLAN_RESUME_STEP = 'plan';

export const PAYMENT_RETURN_PARAMS = {
    kind: 'payment_return',
    result: 'result',
    mode: 'mode',
    sessionId: 'session_id',
    returnWorkspaceId: 'return_workspace_id',
    returnPath: 'return_path',
    planId: 'planId',
    planCode: 'planCode',
    planName: 'planName',
    billingCycle: 'billingCycle',
    onboardingTrialSetup: 'onboarding_trial_setup',
} as const;

export type PaymentReturnKind = 'subscription' | 'credit_topup';
export type PaymentReturnResult = 'success' | 'cancel';
export type PaymentReturnMode = 'onboarding' | 'upgrade';
export type PaymentReturnBillingCycle = 'monthly' | 'yearly';

export interface PaymentReturnState {
    kind: PaymentReturnKind | null;
    result: PaymentReturnResult | null;
    mode: PaymentReturnMode;
    sessionId: string | null;
    workspaceId: string | null;
    returnPath: string;
    planId: string | null;
    planCode: string | null;
    planName: string | null;
    billingCycle: PaymentReturnBillingCycle | null;
    onboardingTrialSetup: boolean;
}

interface BuildPaymentReturnUrlParams {
    origin: string;
    kind: PaymentReturnKind;
    result: PaymentReturnResult;
    mode?: PaymentReturnMode;
    workspaceId?: string | null;
    returnPath?: string | null;
    planId?: string | null;
    planCode?: string | null;
    planName?: string | null;
    billingCycle?: string | null;
    includeCheckoutSessionId?: boolean;
    onboardingTrialSetup?: boolean;
}

interface RuntimeAccessOptions {
    disableCloudRuntime?: boolean;
}

interface BuildPaymentReturnOnboardingSuccessRouteParams extends RuntimeAccessOptions {
    workspaceId: string;
    planCode?: string | null;
    planName?: string | null;
    billingCycle?: string | null;
    onboardingTrialSetup?: boolean;
}

interface BuildFinalPaymentRouteParams extends RuntimeAccessOptions {
    state: PaymentReturnState;
    creditTopupChecked?: boolean;
}

const ADMIN_BASENAME = '/admin';
export const PAYMENT_RETURN_UPGRADE_PATH = '/billing';

export const buildPaymentReturnOnboardingSuccessRoute = ({
    workspaceId,
    planCode,
    planName,
    billingCycle,
    onboardingTrialSetup = false,
}: BuildPaymentReturnOnboardingSuccessRouteParams): string => {
    const searchParams = new URLSearchParams();
    if (workspaceId.trim()) {
        searchParams.set('workspace_id', workspaceId.trim());
    }
    searchParams.set('onboarding_success', 'true');
    if (planCode?.trim()) {
        searchParams.set('planCode', planCode.trim());
    }
    if (planName?.trim()) {
        searchParams.set('planName', planName.trim());
    }
    const normalizedBillingCycle = readKnownBillingCycle(billingCycle || null);
    if (normalizedBillingCycle) {
        searchParams.set(PAYMENT_RETURN_PARAMS.billingCycle, normalizedBillingCycle);
    }
    if (onboardingTrialSetup) {
        searchParams.set(PAYMENT_RETURN_PARAMS.onboardingTrialSetup, 'true');
    }

    const nextSearch = searchParams.toString();
    return `/profile${nextSearch ? `?${nextSearch}` : ''}`;
};

const readKnownKind = (value: string | null): PaymentReturnKind | null => {
    if (value === 'subscription' || value === 'credit_topup') {
        return value;
    }

    return null;
};

const readKnownResult = (value: string | null): PaymentReturnResult | null => {
    if (value === 'success' || value === 'cancel') {
        return value;
    }

    return null;
};

const readKnownMode = (value: string | null): PaymentReturnMode => (
    value === 'onboarding' ? 'onboarding' : 'upgrade'
);

const readKnownBillingCycle = (value: string | null): PaymentReturnBillingCycle | null => {
    if (value === 'monthly' || value === 'yearly') {
        return value;
    }

    return null;
};

const readReturnValue = (
    searchParams: URLSearchParams,
    key: string,
    fallback?: string | null,
): string | null => {
    const urlValue = searchParams.get(key)?.trim();
    if (urlValue) {
        return urlValue;
    }

    const fallbackValue = fallback?.trim();
    return fallbackValue || null;
};

export const isUnresolvedCheckoutSessionId = (
    value?: string | null,
): boolean => {
    const normalized = (value || '').trim();
    return !normalized || normalized === STRIPE_CHECKOUT_SESSION_ID_TEMPLATE;
};

export const normalizePaymentReturnPath = (
    returnPath?: string | null,
): string => {
    const trimmed = (returnPath || '').trim();
    if (!trimmed) {
        return '/';
    }

    let pathname = trimmed;
    try {
        if (/^https?:\/\//i.test(trimmed)) {
            pathname = new URL(trimmed).pathname;
        }
    } catch {
        return '/';
    }

    if (!pathname.startsWith('/') || pathname.startsWith('//')) {
        return '/';
    }

    if (pathname === ADMIN_BASENAME) {
        return '/';
    }

    if (pathname.startsWith(`${ADMIN_BASENAME}/`)) {
        return pathname.slice(ADMIN_BASENAME.length) || '/';
    }

    return pathname;
};

export const appendRawCheckoutSessionPlaceholder = (url: URL): string => {
    const baseUrl = url.toString();
    const separator = url.search ? '&' : '?';
    return `${baseUrl}${separator}${PAYMENT_RETURN_PARAMS.sessionId}=${STRIPE_CHECKOUT_SESSION_ID_TEMPLATE}`;
};

export const buildPaymentReturnUrl = ({
    origin,
    kind,
    result,
    mode = 'upgrade',
    workspaceId,
    returnPath,
    planId,
    planCode,
    planName,
    billingCycle,
    includeCheckoutSessionId = false,
    onboardingTrialSetup = false,
}: BuildPaymentReturnUrlParams): string => {
    const url = new URL(PAYMENT_RETURN_PUBLIC_PATH, origin);
    url.searchParams.set(PAYMENT_RETURN_PARAMS.kind, kind);
    url.searchParams.set(PAYMENT_RETURN_PARAMS.result, result);
    url.searchParams.set(PAYMENT_RETURN_PARAMS.returnPath, normalizePaymentReturnPath(returnPath));

    if (kind === 'subscription') {
        url.searchParams.set(PAYMENT_RETURN_PARAMS.mode, mode);
    }

    if (workspaceId) {
        url.searchParams.set(PAYMENT_RETURN_PARAMS.returnWorkspaceId, workspaceId);
    }

    if (planId) {
        url.searchParams.set(PAYMENT_RETURN_PARAMS.planId, planId);
    }

    if (planCode) {
        url.searchParams.set(PAYMENT_RETURN_PARAMS.planCode, planCode);
    }

    if (planName) {
        url.searchParams.set(PAYMENT_RETURN_PARAMS.planName, planName);
    }

    const normalizedBillingCycle = readKnownBillingCycle(billingCycle || null);
    if (normalizedBillingCycle) {
        url.searchParams.set(PAYMENT_RETURN_PARAMS.billingCycle, normalizedBillingCycle);
    }

    if (onboardingTrialSetup) {
        url.searchParams.set(PAYMENT_RETURN_PARAMS.onboardingTrialSetup, 'true');
    }

    return includeCheckoutSessionId
        ? appendRawCheckoutSessionPlaceholder(url)
        : url.toString();
};

export const readPaymentReturnState = (
    searchParams: URLSearchParams,
    fallbackSessionId?: string | null,
): PaymentReturnState => {
    const sessionId = searchParams.get(PAYMENT_RETURN_PARAMS.sessionId);

    return {
        kind: readKnownKind(searchParams.get(PAYMENT_RETURN_PARAMS.kind)),
        result: readKnownResult(searchParams.get(PAYMENT_RETURN_PARAMS.result)),
        mode: readKnownMode(searchParams.get(PAYMENT_RETURN_PARAMS.mode)),
        sessionId: isUnresolvedCheckoutSessionId(sessionId)
            ? fallbackSessionId || null
            : sessionId,
        workspaceId: readReturnValue(searchParams, PAYMENT_RETURN_PARAMS.returnWorkspaceId),
        returnPath: normalizePaymentReturnPath(searchParams.get(PAYMENT_RETURN_PARAMS.returnPath)),
        planId: readReturnValue(searchParams, PAYMENT_RETURN_PARAMS.planId),
        planCode: readReturnValue(searchParams, PAYMENT_RETURN_PARAMS.planCode),
        planName: readReturnValue(searchParams, PAYMENT_RETURN_PARAMS.planName),
        billingCycle: readKnownBillingCycle(searchParams.get(PAYMENT_RETURN_PARAMS.billingCycle)),
        onboardingTrialSetup: searchParams.get(PAYMENT_RETURN_PARAMS.onboardingTrialSetup) === 'true',
    };
};

export const shouldVerifySubscriptionReturnBeforeFinalRoute = (
    state: Pick<PaymentReturnState, 'kind' | 'result' | 'mode'>,
    productCode?: string | null,
): boolean => {
    return state.kind === 'subscription'
        && state.result === 'success'
        && state.mode === 'upgrade'
        && !isPluginProduct(productCode);
};

export const buildFinalPaymentRoute = ({
    state,
    creditTopupChecked = false,
    disableCloudRuntime,
}: BuildFinalPaymentRouteParams): string => {
    if (state.kind === 'subscription' && state.result === 'success' && state.mode === 'onboarding') {
        if (!state.workspaceId) {
            return '/onboarding?pay_result=success';
        }

        writePaymentReturnWorkspaceId(state.workspaceId);
        return buildPaymentReturnOnboardingSuccessRoute({
            workspaceId: state.workspaceId,
            planCode: state.planCode,
            planName: state.planName,
            billingCycle: state.billingCycle,
            onboardingTrialSetup: state.onboardingTrialSetup,
            disableCloudRuntime,
        });
    }

    if (state.kind === 'credit_topup') {
        const route = new URL(state.returnPath || '/usage', 'https://salerio.local');
        route.searchParams.set('payment_modal', 'usage');
        route.searchParams.set('topup_result', state.result || 'cancel');
        if (state.sessionId) {
            route.searchParams.set('session_id', state.sessionId);
        }
        if (state.workspaceId) {
            route.searchParams.set('workspace_id', state.workspaceId);
        }
        if (creditTopupChecked) {
            route.searchParams.set('topup_checked', 'true');
        }

        return `${route.pathname}${route.search}`;
    }

    if (state.kind === 'subscription' && state.result === 'success' && state.workspaceId) {
        writePaymentReturnWorkspaceId(state.workspaceId);
    }

    const route = new URL(
        state.kind === 'subscription' && state.mode === 'upgrade'
            ? PAYMENT_RETURN_UPGRADE_PATH
            : state.returnPath || '/',
        'https://salerio.local',
    );
    route.searchParams.set('pay_result', state.result || 'cancel');
    if (state.kind === 'subscription' && state.result === 'success') {
        route.searchParams.set('payment_checked', 'true');
    }
    if (state.workspaceId) {
        route.searchParams.set('workspace_id', state.workspaceId);
    }
    if (state.planCode) {
        route.searchParams.set('planCode', state.planCode);
    }
    if (state.planName) {
        route.searchParams.set('planName', state.planName);
    }
    if (state.billingCycle) {
        route.searchParams.set(PAYMENT_RETURN_PARAMS.billingCycle, state.billingCycle);
    }
    if (
        state.kind === 'subscription'
        && state.mode === 'onboarding'
        && state.result === 'cancel'
    ) {
        route.searchParams.set('resume_step', ONBOARDING_PLAN_RESUME_STEP);
    }

    return `${route.pathname}${route.search}`;
};
